import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { CustomEquipmentChange, CustomEquipmentOptionsView } from "@slfinrtl/ui-bff-routes"
import { getCustomEquipmentOptionsRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import type { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { propertiesOf } from "src/common/property"
import { getMarketLanguageTranslations } from "src/common/translations"
import { useBff } from "src/hooks"
import type { AddCustomEquipmentProps } from "./components/AddCustomEquipment"
import { addCustomEquipmentMessages } from "./components/addCustomEquipment.messages"

export interface CustomEquipmentForm {
	category: string
	name: Record<string, string>
	tags: string[]
}

export interface RichCustomEquipment {
	category: { key: string; name: string }
	name: Record<string, string>
	tags: string[]
}

export const useAddCustomEquipment = (props: {
	onAddCustomEquipment: (customEquipment: RichCustomEquipment) => Promise<void> | void
	onFormClosed?: () => void
}): {
	form: UseFormReturn<CustomEquipmentForm>
	onSubmit: SubmitHandler<CustomEquipmentChange>
	props: AddCustomEquipmentProps | undefined
} => {
	const { onAddCustomEquipment, onFormClosed } = props
	const { availableLocales } = useDealerContext()

	const [customEquipmentOptionsView, setCustomEquipmentOptionsView] = useState<CustomEquipmentOptionsView>({
		equipmentCategories: [],
		tagCategories: [],
	})
	const fetchCustomEquipmentOptions = useBff(
		getCustomEquipmentOptionsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {},
		},
		"global",
	)

	useEffect(() => {
		fetchCustomEquipmentOptions().then(setCustomEquipmentOptionsView)
	}, [fetchCustomEquipmentOptions])

	const [hasSubmitFailed, setHasSubmitFailed] = useState(false)
	const onSubmit: SubmitHandler<CustomEquipmentForm> = async (formState) => {
		try {
			await onAddCustomEquipment({
				category: {
					key: formState.category,
					name:
						customEquipmentOptionsView.equipmentCategories.find(
							(category) => category.key === formState.category,
						)?.name ?? formState.category,
				},
				name: getMarketLanguageTranslations(availableLocales, formState.name),
				tags: formState.tags,
			})
			onFormClosed?.()
		} catch {
			setHasSubmitFailed(true)
		}
	}

	const form = useForm<CustomEquipmentForm>({
		defaultValues: {
			category: "",
			name: getMarketLanguageTranslations(availableLocales, {}),
			tags: [],
		},
	})

	const onCancel = () => {
		form.reset()
		onFormClosed?.()
	}

	const { formatDisplayName, formatMessage } = useIntl()

	return {
		form,
		onSubmit,
		props: {
			fields: {
				category: {
					filter: true,
					label: formatMessage(addCustomEquipmentMessages.equipmentCategory),
					name: customEquipmentFormProperties.category._path,
					options: customEquipmentOptionsView.equipmentCategories.map((category) => ({
						label: category.name,
						value: category.key,
					})),
					required: true,
				},
				name: availableLocales.map((languageTag) => ({
					label: `${formatMessage(addCustomEquipmentMessages.customEquipmentName)} - ${formatDisplayName(
						languageTag,
						{
							type: "language",
						},
					)}`,
					name: `${customEquipmentFormProperties.name._path}.${languageTag}`,
					required: true,
					type: "text",
					validate: (value: string | number) =>
						value.toString() !== value.toString().toUpperCase() ||
						formatMessage(commonMessages.onlyCapitalLetters),
				})),
				tags: {
					label: formatMessage(addCustomEquipmentMessages.equipmentTags),
					name: customEquipmentFormProperties.tags._path,
					options: customEquipmentOptionsView.tagCategories.map(({ tagCategory, tags }) => ({
						label: tagCategory,
						options: tags.map(({ key, name }) => ({
							label: name,
							value: key,
						})),
					})),
				},
			},
			hasSubmitFailed,
			isSubmitting: form.formState.isSubmitting,
			onCancel,
			setHasSubmitFailed,
		},
	}
}

const customEquipmentFormProperties = propertiesOf<CustomEquipmentForm>()
